import React, { useCallback, useEffect, useState } from 'react';
import { fetchStoreStatus, patchStoreStatus } from '../api/adminApi';
import { showToast } from '../lib/toast';

export function StoreOpenSwitch() {
  const [isOpen, setIsOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const status = await fetchStoreStatus();
        if (mounted && status && typeof status.isOpen === 'boolean') {
          setIsOpen(status.isOpen);
        }
      } catch {
        if (mounted) showToast('Could not load store status', 'danger');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const handleToggle = useCallback(async () => {
    if (saving || loading) return;
    const next = !isOpen;
    if (
      !next &&
      !window.confirm(
        'Close the cafe? Customers on the app will be notified immediately that you are closed for now.',
      )
    ) {
      return;
    }
    setSaving(true);
    const prev = isOpen;
    setIsOpen(next);
    try {
      const status = await patchStoreStatus({ isOpen: next });
      setIsOpen(status?.isOpen ?? next);
      showToast(
        status?.isOpen
          ? 'Cafe is open — app users can order'
          : 'Cafe is closed — app users were notified',
        status?.isOpen ? 'success' : 'warning',
      );
    } catch (err) {
      setIsOpen(prev);
      showToast(err?.message || 'Failed to update store status', 'danger');
    } finally {
      setSaving(false);
    }
  }, [isOpen, loading, saving]);

  const label = isOpen ? 'Open' : 'Closed';
  const statusHint = isOpen ? 'Taking orders' : 'Closed for now';

  return (
    <div
      className={`store-open-switch d-flex align-items-center gap-2${isOpen ? ' is-open' : ' is-closed'}${saving ? ' is-saving' : ''}`}
      title={statusHint}
    >
      <div className="store-open-switch-text text-end d-none d-xl-block">
        <span className="store-open-switch-label fw-semibold">{label}</span>
        <span className="store-open-switch-sub d-block small text-secondary">{statusHint}</span>
      </div>
      <button
        type="button"
        role="switch"
        className="store-open-switch-control"
        aria-checked={isOpen}
        aria-label={isOpen ? 'Cafe is open; click to close' : 'Cafe is closed; click to open'}
        disabled={loading || saving}
        onClick={() => void handleToggle()}
      >
        <span className="store-open-switch-track" aria-hidden>
          <span className="store-open-switch-thumb" />
        </span>
      </button>
    </div>
  );
}
